import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSettings } from "../context/SettingsContext";
import { X, LifeBuoy, ChevronDown, PlayCircle, CalendarDays, Mail } from "lucide-react";

const FAQS = [
  { q: "How do I earn points and climb the leaderboard?", a: "You earn points for completing chapters, uploading drill videos, logging daily journal sessions, keeping your streak alive, and finishing the weekly session. The more consistent you are, the higher you climb." },
  { q: "How do I upload a video for coach feedback?", a: "In the Hub, scroll to 'My Video Proofs', tap Choose Video, film a short 10–20s clip of your move (whole body in frame, ball visible, good lighting), add the move name, and upload. Coach Barker reviews it and leaves you feedback — including voice notes." },
  { q: "Where do I see the coach's feedback?", a: "When Coach leaves feedback, the bell in your Hub header shows a red dot. Tap it to jump to your videos, where you'll see the reaction emoji and can play the coach's voice note." },
  { q: "What's the difference between the Blueprint, the Technical Track and Elite?", a: "Volume 1: Ball Mastery Blueprint gives you the full training system plus one free coach review. The Technical Track adds unlimited coach reviews. Elite adds a personalised monthly roadmap and private coach videos made just for you." },
  { q: "How do family / sibling profiles work?", a: "One purchase covers up to 5 players. Use 'Switch Player' in the Hub header to add and jump between profiles — each child keeps their own name, jersey, points and leaderboard rank." },
  { q: "How do I keep my streak alive?", a: "Log a training session in your Daily Journal every day. Miss a day and the streak resets — the Hub warns you when your streak is at risk so you can log a quick session before the day ends." },
];

export default function HelpCenter({ onClose }) {
  const { helpVideos = [], bookingLink, coachEmail } = useSettings();
  const [open, setOpen] = useState(0);

  return (
    <div className="fixed inset-0 z-[90] bg-black/80 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="help-center-modal">
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-lg my-8 bg-[#141414] border border-white/12 rounded-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="help-close" className="absolute top-4 right-4 text-white/50 hover:text-white"><X size={20} /></button>
        <div className="flex items-center gap-2 mb-1"><LifeBuoy size={16} className="text-[#C41E24]" /><span className="text-[10px] uppercase tracking-widest text-[#C41E24] font-bold">Help Center</span></div>
        <h2 className="font-cover text-3xl sm:text-4xl uppercase tracking-tight">Need a hand?</h2>
        <p className="text-white/55 mt-2 text-sm">Quick answers and walkthrough videos to get the most out of your training.</p>

        {/* Walkthrough videos */}
        {helpVideos.length > 0 && (
          <div className="mt-6" data-testid="help-videos">
            <div className="text-xs font-bold uppercase tracking-widest text-white/60 mb-3">Walkthrough Videos</div>
            <div className="space-y-2">
              {helpVideos.map((v, i) => (
                <a key={i} href={v.url} target="_blank" rel="noopener noreferrer"
                  data-testid={`help-video-${i}`}
                  className="flex items-center gap-3 rounded-lg border border-white/10 bg-[#0d0d0d] px-4 py-3 hover:border-[#C41E24]/60 transition-colors group">
                  <PlayCircle size={20} className="text-[#C41E24] shrink-0" />
                  <span className="text-sm text-white/85 group-hover:text-white flex-1 min-w-0 truncate">{v.title || "Walkthrough video"}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white/35 group-hover:text-[#C41E24]">Watch</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* FAQ accordion */}
        <div className="mt-6" data-testid="help-faq">
          <div className="text-xs font-bold uppercase tracking-widest text-white/60 mb-3">Frequently Asked</div>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-[#0d0d0d] overflow-hidden">
                <button data-testid={`faq-q-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
                  <span className="text-sm font-semibold text-white/90">{f.q}</span>
                  <ChevronDown size={16} className={`text-white/40 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden">
                      <p data-testid={`faq-a-${i}`} className="px-4 pb-4 text-sm text-white/55 leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>

        {/* Contact actions */}
        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          {bookingLink && (
            <a href={bookingLink} target="_blank" rel="noopener noreferrer" data-testid="help-book-session"
              className="flex-1 flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-3 rounded-md transition-colors">
              <CalendarDays size={14} /> Book a Session
            </a>
          )}
          <a href={`mailto:${coachEmail || "d.barker@nextosoccer.com"}`} data-testid="help-email-coach"
            className="flex-1 flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 text-white/80 hover:text-white text-xs font-bold uppercase tracking-wider py-3 rounded-md transition-colors">
            <Mail size={14} /> Email Coach
          </a>
        </div>
      </motion.div>
    </div>
  );
}
